import { Alert, Box, Button, Stack, Typography, Menu, MenuItem, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx/xlsx';
import { Loader } from '../../components/Loader/Loader';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useAppDispatch } from '../../redux/hooks';
import { fetchDashboard } from '../../redux/modules/analytics/analytics.actions';
import { analyticsSelector } from '../../redux/modules/analytics/analytics.selectors';
import { AdjustReports } from './AdjustReports';

export function DashboardTab() {
  const { error, dashboardData, fetchDashboardStatus } = useSelector(analyticsSelector) as any;
  const dispatch = useAppDispatch();
  const [dateRange, setDateRange] = useState<{ start_date: string | null; end_date: string | null }>({
    start_date: null,
    end_date: null,
  });
  const isLoading = fetchDashboardStatus === fetchStatuses.pending;
  const hasError = fetchDashboardStatus === fetchStatuses.rejected;
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const csvLinkRef = useRef<CSVLink | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  useEffect(() => {
    dispatch(fetchDashboard(dateRange as { start_date: string; end_date: string }));
  }, [dispatch, dateRange]);

  // Reset filters when component mounts
  useEffect(() => {
    setDateRange({
      start_date: null,
      end_date: null,
    });
  }, []);

  const handleRetry = () => {
    dispatch(fetchDashboard(dateRange as { start_date: string; end_date: string }));
  };

  function formatKey(key: string) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function transformObject(obj: any) {
    return Object.entries(obj).map(([k, v]) => {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        return {
          title: formatKey(k),
          value: transformObject(v),
        };
      }
      return {
        title: formatKey(k),
        value: v,
      };
    });
  }

  function transformData(data: any) {
    return Object.entries(data)
      .map(([category, values]) => {
        // Skip timestamp and filters_applied objects
        if (category === 'timestamp' || category === 'filters_applied') return null;

        if (values && typeof values === 'object' && !Array.isArray(values)) {
          return {
            mainTitle: formatKey(category),
            cards: transformObject(values),
          };
        }

        return {
          mainTitle: formatKey(category),
          cards: [{ title: formatKey(category), value: values }],
        };
      })
      .filter(Boolean);
  }

  const result = transformData(dashboardData || {});

  function buildHorizontalRows(sections: any[]): {
    rows: any[][];
    merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }>;
    colspans: number[];
  } {
    const headerRow1: string[] = [];
    const headerRow2: string[] = [];
    const valuesRow: (string | number)[] = [];
    const merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }> = [];
    const colspans: number[] = [];

    const leavesForSection = (section: any) => {
      const leaves: Array<{ label: string; value: any }> = [];
      section?.cards?.forEach((card: any) => {
        if (Array.isArray(card?.value)) {
          card.value.forEach((nested: any) => {
            const combinedLabel = `${card?.title} - ${nested?.title}`;
            leaves.push({ label: combinedLabel, value: nested?.value });
          });
        } else {
          leaves.push({ label: card?.title, value: card?.value });
        }
      });
      return leaves;
    };

    sections?.forEach((section: any) => {
      const leaves = leavesForSection(section);
      const title = section?.mainTitle ?? '';
      const count = Math.max(leaves.length, 1);
      const startIndex = headerRow1.length;
      headerRow1.push(title);
      for (let i = 1; i < count; i += 1) headerRow1.push('');
      if (count > 1) merges.push({ s: { r: 0, c: startIndex }, e: { r: 0, c: startIndex + count - 1 } });
      colspans.push(count);
      if (leaves.length === 0) {
        headerRow2.push('');
        valuesRow.push('');
      } else {
        leaves.forEach((leaf) => {
          headerRow2.push(leaf.label ?? '');
          valuesRow.push(leaf.value ?? '');
        });
      }
    });

    return { rows: [headerRow1, headerRow2, valuesRow], merges, colspans };
  }

  const handleExportCSV = () => {
    const { rows } = buildHorizontalRows(result);
    setCsvRows(rows);
    setTimeout(() => {
      csvLinkRef.current?.link?.click();
    }, 0);
  };

  const handleExportExcel = () => {
    const { rows, merges } = buildHorizontalRows(result);
    // Create workbook & worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    // Apply merged cells if needed
    if (merges.length) {
      (worksheet as any)['!merges'] = merges;
    }
    // Append sheet and save as .xlsx
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dashboard');
    XLSX.writeFile(workbook, `dashboard-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleOpenExportMenu = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportAnchorEl(null);
  };
  return (
    <>
      <Stack spacing={2} p={3}>
        <Box sx={{ pb: 2 }}>
          <Typography variant="body2">Filter by date</Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <DatePicker
            label="From Date"
            value={dateRange.start_date ? new Date(`${dateRange.start_date}T00:00:00`) : null}
            onChange={(date) => {
              if (date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setDateRange({ ...dateRange, start_date: `${year}-${month}-${day}` });
              } else {
                setDateRange({ ...dateRange, start_date: null });
              }
            }}
            slotProps={{
              textField: {
                margin: 'normal',
              },
            }}
          />
          <DatePicker
            label="To Date"
            value={dateRange.end_date ? new Date(`${dateRange.end_date}T00:00:00`) : null}
            onChange={(date) => {
              if (date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setDateRange({ ...dateRange, end_date: `${year}-${month}-${day}` });
              } else {
                setDateRange({ ...dateRange, end_date: null });
              }
            }}
            slotProps={{
              textField: {
                margin: 'normal',
              },
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleOpenExportMenu}>
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportMenuOpen}
              onClose={handleCloseExportMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseExportMenu();
                  handleExportCSV();
                }}
              >
                CSV
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseExportMenu();
                  handleExportExcel();
                }}
              >
                Excel
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
        <Loader isLoading={isLoading}>
          <Box>
            {hasError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                }
                sx={{ mb: 3 }}
              >
                Failed to load dashboard data. {error?.message || 'Please try again.'}
              </Alert>
            )}

            {!hasError &&
              result?.map((section) => (
                <Box
                  key={section?.mainTitle}
                  mb={4}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {section?.mainTitle}
                  </Typography>
                  <Grid container spacing={2}>
                    {section?.cards.map((card) => (
                      <Grid
                        item
                        xs={12}
                        sm={Array.isArray(card.value) ? 12 : 6}
                        md={Array.isArray(card.value) ? 12 : 3}
                        key={card.title}
                      >
                        <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 0, p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {card.title}
                          </Typography>

                          {Array.isArray(card.value) ? (
                            <Box mt={1}>
                              <Grid container spacing={2}>
                                {card.value.map((nested: any) => (
                                  <Grid item xs={12} sm={6} md={6} key={nested.title}>
                                    <Box sx={{ p: 1, borderColor: 'divider', borderRadius: 1 }}>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {nested.title}:
                                      </Typography>
                                      <Typography variant="body1" fontWeight="medium">
                                        {nested.value}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          ) : (
                            <Typography variant="body1">{card.value}</Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
          </Box>
        </Loader>
      </Stack>
      <CSVLink
        data={csvRows}
        filename={`dashboard-${new Date().toISOString().slice(0, 10)}.csv`}
        className="hidden"
        ref={csvLinkRef as any}
        target="_blank"
      />
      <AdjustReports dateRange={dateRange} />
    </>
  );
}
